import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { fetchUserData } from "../services/overviewApiService"; 
import { fetchMockUserData } from "../services/mockService";

interface Student {
  accountId: number;
  firstName: string;
  lastName: string;
  grade: {
    printLabel: string;
    gradeId: number;
    value: number;
  };
  schoolDetails: {
    name: string;
    shortName: string;
    schoolId: number;
  };
}

interface OverviewContextType {
  loggedInUser: any;
  students: Student[];
  schoolFullName: string;
  formattedStudentNames: string;
  userRoleVal : string;
  loading: boolean;
  error: string | null;
  hasNonHighSchoolStudents: boolean;
  identityId: string | null;
  schoolType: string;
  schoolShortName: string;
}

const OverviewContext = createContext<OverviewContextType | undefined>(undefined);

export const OverviewProvider = ({ children }: { children: ReactNode }) => {
    const { authState } = useOktaAuth();
    const [loggedInUser, setLoggedInUser] = useState<any>(null);
    const [students, setStudents] = useState<Student[]>([]);
    const [schoolFullName, setSchoolFullName] = useState<string>("");
    const [formattedStudentNames, setFormattedStudentNames] = useState<string>('');
    const [userRoleVal, setUserRole] = useState<string>('');
    const [identityId, setIdentityId] = useState<string | null>(null);  

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    const [hasNonHighSchoolStudents, setHasNonHighSchoolStudents] = useState<boolean>(false);
    const [schoolType, setSchoolType] = useState<string>("");
    const [schoolShortName, setSchoolShortName] = useState<string>("");

    useEffect(() => {
        if (!authState || !authState.isAuthenticated || loggedInUser) return;
        const fetchData = async () => {
            try {
                
                const data = await fetchUserData(authState.accessToken?.accessToken);
                setLoggedInUser(data.user);
                setStudents(data.students.studentsList);
                setUserRole(data.user.role);
                setIdentityId(data.user.identityId || null);
                setSchoolFullName(data.students.schoolFullName || "");
                setSchoolShortName(data.students.schoolShortName || "");
                setSchoolType(data.students.schoolType || ""); 
                
                // immediately after setting students 
                // Check if any student from Elementary/Middle School
                const nonHighSchoolStudents = data.students.studentsList.some(
                    (student: Student) => student.grade.value < 9
                );
                setHasNonHighSchoolStudents(nonHighSchoolStudents);
                // console.log(data);

            } catch (err) {
                setError("Failed to fetch overview API");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [authState, loggedInUser]);

    // Update formattedStudentNames when students data changes
    useEffect(() => {
        setFormattedStudentNames(formatStudentNames(students));
    }, [students]);

    // Helper function to format student names
    const formatStudentNames = (students: any[]): string => {
        const names = students.map((student) => student.firstName);
        if (names.length === 1) return names[0];
        if (names.length === 2) return names.join(' and ');
        return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
    };
    return (
        <OverviewContext.Provider value={{ loggedInUser, students, schoolFullName, schoolShortName, schoolType, formattedStudentNames: formatStudentNames(students), userRoleVal, identityId, loading, error, hasNonHighSchoolStudents }}>
            {children}
        </OverviewContext.Provider>
    );
};

export const useOverviewContext = () => {
  const context = useContext(OverviewContext);
  if (!context) {
    throw new Error("useOverviewContext must be used within an OverviewProvider");
  }
  return context;
};
